import { FC, useEffect, useState } from "react";
import _ from "lodash";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { SectionTitle } from "../../common/components/SectionTitle";
import { Asterisk } from "../../common/components/Asterisk";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { useCreateTaskCounterTemplate } from "../hooks/useCreateTaskCounterTemplate";
import { ImportTaskCounterModal } from "./ImportTaskCounterModal";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const formControlStyle = {
  marginTop: "20px",
};

const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "60px",
};

type Option = {
  id: string;
  title: string;
};

const TASK_TYPES = [
  { id: 1, name: "Label" },
  { id: 2, name: "Task" },
];

const UNIT_OF_MEASURES = [
  { id: 1, name: "Items" },
  { id: 2, name: "Minutes" },
];

export const CreateTaskCounterTemplate: FC = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm();

  const navigate = useNavigate();

  const {
    mutationCreateTemplate,
    mutationGetJobTitles,
    mutationGetOrganizations,
    parentCompanies,
    organizations,
    jobTitles,
  } = useCreateTaskCounterTemplate();

  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedJobTitles, setSelectedJobTitles] = useState<Option[]>([]);
  const [isShowImportModal, setIsShowImportModal] = useState(false);

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "admin";

  const parentCompanyId = localStorage.getItem("parentCompanyId") ?? "";

  useEffect(() => {
    if (isRoleAdmin && parentCompanyId) {
      setValue("parentCompanyId", parentCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoleAdmin, parentCompanyId, parentCompanies]);

  const submitCreateTemplate = (data: any) => {
    const payload = {
      organizationId: data.organization,
      taskName: data.taskName,
      taskOrder: data.taskOrder ? parseInt(data.taskOrder) : null,
      jobTitleIds: _.map(selectedJobTitles, "id"),
      taskType: data.taskType,
      unitOfMeasure: data.unitOfMeasure,
    };

    mutationCreateTemplate.mutate(payload);
  };

  const handleCancel = () => {
    navigate("/task-counter");
  };

  return (
    <div className="mt-9">
      <div className="flex flex-row items-center justify-between">
        <SectionTitle name="Create Task Counter Template" />
        <ButtonCancelA
          name={"Import from File"}
          onHandleClick={() => {
            setIsShowImportModal(true);
          }}
        />
      </div>

      <Box sx={{ minWidth: 120, marginTop: "60px" }}>
        <form onSubmit={handleSubmit(submitCreateTemplate)}>
          <span className="text-sub-title">Task Summary Information</span>
          <div className={"flex flex-col"}>
            <div className="mr-10 ml-10">
              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">
                  Company <Asterisk />
                </label>
                <Controller
                  control={control}
                  name="parentCompanyId"
                  rules={{ required: "Company is required" }}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="parentCompanyId"
                      value={value ?? ""}
                      onChange={(e) => {
                        setValue("parentCompanyId", e.target.value);
                        clearErrors("parentCompanyId");

                        // Reset organization and job titles when company changes
                        setValue("organization", "");
                        setSelectedOrganizationId("");
                        setSelectedJobTitles([]);
                        setValue("jobTitle", []);

                        mutationGetOrganizations.mutate(e.target.value);
                      }}
                      style={{ height: "38px" }}
                      disabled={isRoleAdmin}
                    >
                      {(parentCompanies ?? []).map((item: any) => (
                        <MenuItem key={`company-${item.value}`} value={item.value}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                ></Controller>
                <FormHelperText sx={{ color: "red" }}>
                  {control.getFieldState("parentCompanyId").error?.message}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">
                  Organization <Asterisk />
                </label>
                <Controller
                  control={control}
                  name="organization"
                  rules={{ required: "Organization is required" }}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="organization"
                      value={value ?? ""}
                      onChange={(e) => {
                        setValue("organization", e.target.value);
                        setSelectedOrganizationId(e.target.value);
                        clearErrors("organization");

                        // Reset job titles when organization changes
                        setSelectedJobTitles([]);
                        setValue("jobTitle", []);

                        mutationGetJobTitles.mutate(e.target.value);
                      }}
                      style={{ height: "38px" }}
                    >
                      {(organizations ?? []).map((item: any) => (
                        <MenuItem key={`counter-org-${item.id}`} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                ></Controller>
                <FormHelperText sx={{ color: "red" }}>
                  {control.getFieldState("organization").error?.message}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">
                  Task Name <Asterisk />
                </label>
                <TextField
                  {...register("taskName", {
                    required: "Task Name is required",
                    maxLength: {
                      value: 255,
                      message: "Task Name must not exceed 255 characters",
                    },
                  })}
                  sx={{
                    "& .MuiInputBase-input": {
                      height: "5px",
                    },
                  }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  {errors.taskName?.message as string}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">
                  Job Title <Asterisk />
                </label>
                <Controller
                  control={control}
                  name="jobTitle"
                  rules={{
                    validate: () =>
                      selectedJobTitles.length > 0 ||
                      "Job Title is required",
                  }}
                  defaultValue={[]}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Autocomplete
                      multiple
                      id="job-title-select"
                      options={jobTitles ?? []}
                      disableCloseOnSelect
                      value={selectedJobTitles}
                      getOptionLabel={(option: Option) => option.title}
                      isOptionEqualToValue={(option, value) =>
                        value === undefined || option.id === value.id
                      }
                      onChange={(event, data) => {
                        setSelectedJobTitles(data);
                        setValue("jobTitle", data);
                        clearErrors("jobTitle");
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.title}
                        </li>
                      )}
                      style={{ width: "100%" }}
                      renderInput={(params) => <TextField {...params} />}
                      disabled={!selectedOrganizationId}
                    />
                  )}
                />
                <FormHelperText sx={{ color: "red" }}>
                  {control.getFieldState("jobTitle").error?.message}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">
                  Task Unit of Measure <Asterisk />
                </label>
                <Controller
                  control={control}
                  name="unitOfMeasure"
                  rules={{ required: "Unit of Measure is required" }}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="unitOfMeasure"
                      value={value ?? ""}
                      onChange={(e) => {
                        setValue("unitOfMeasure", e.target.value);
                        clearErrors("unitOfMeasure");
                      }}
                      style={{ height: "38px" }}
                    >
                      {UNIT_OF_MEASURES.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                ></Controller>
                <FormHelperText sx={{ color: "red" }}>
                  {control.getFieldState("unitOfMeasure").error?.message}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">
                  Task Type <Asterisk />
                </label>
                <Controller
                  control={control}
                  name="taskType"
                  rules={{ required: "Task Type is required" }}
                  defaultValue=""
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="taskType"
                      value={value ?? ""}
                      onChange={(e) => {
                        setValue("taskType", e.target.value);
                        clearErrors("taskType");
                      }}
                      style={{ height: "38px" }}
                    >
                      {TASK_TYPES.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                ></Controller>
                <FormHelperText sx={{ color: "red" }}>
                  {control.getFieldState("taskType").error?.message}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth sx={formControlStyle}>
                <label className="font-bold">Task Order</label>
                <TextField
                  type="number"
                  {...register("taskOrder", {
                    validate: (value) => {
                      if (value === "" || value === undefined || value === null)
                        return true;
                      const num = parseInt(value);
                      if (isNaN(num) || num <= 0) {
                        return "Task Order must be a positive number";
                      }
                      return true;
                    },
                  })}
                  sx={{
                    "& .MuiInputBase-input": {
                      height: "5px",
                    },
                  }}
                />
                <FormHelperText sx={{ color: "red" }}>
                  {errors.taskOrder?.message as string}
                </FormHelperText>
              </FormControl>
            </div>
          </div>

          <FormControl fullWidth sx={btnGroupStyle}>
            <ButtonCancelA
              name="Cancel"
              onHandleClick={handleCancel}
              icon={<CancelIcon sx={{ marginRight: "4px" }} />}
            />
            <ButtonSubmitA
              name="Save"
              icon={<SaveIcon sx={{ marginRight: "4px" }} />}
            />
          </FormControl>
        </form>
      </Box>

      <ImportTaskCounterModal
        parentCompanies={parentCompanies}
        companiesName={organizations}
        isShowImportModal={isShowImportModal}
        setIsShowImportModal={setIsShowImportModal}
      />
    </div>
  );
};
